import fplTokens from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { MEDIA_TO_CANVAS_DRAG_EVENT_NAMES } from '@/consts/analytics.consts';
import { color } from '@/colors';
import { SPEAKER_HIGH_ICON_PATHS } from '@/UI/Icons/SpeakerHighIcon';
import { THREE_D_ICON_PATHS } from '@/UI/Icons/ThreeDIcon';
import { TEXT_ICON_PATHS } from '@/UI/Icons/TextIcon';
import { VIDEO_CAMERA_ICON_PATHS } from '@/UI/Icons/VideoCamera';
import { assetThumbnailUrl } from '@/utils/assetUrls';
import type { ResolvedAssets } from '@/types/api/assets';
import type { DragEvent as ReactDragEvent } from 'react';

type MediaToCanvasDragEffectEvent = {
  altKey: boolean;
  clientX: number;
  clientY: number;
  dataTransfer: DataTransfer | null;
  preventDefault(): void;
};

/**
 * Sources that can place a tile onto the canvas as a new import node (`mode`: duplicate or move).
 * Move must cover the same three sources (remove from source + create import); see canvas drop in Flow
 * and `addImportFromMediaSourceAtFlowPosition` / `getMediaToImportItemIndex`.
 */
export enum MediaToCanvasDragSource {
  Import = 'import',
  MediaIterator = 'mediaIterator',
  AiModel = 'aiModel',
}

/** Duplicate: Alt/Option at drag start. Move: default drag (new import node + remove from source). */
export enum MediaToCanvasDragMode {
  Duplicate = 'duplicate',
  Move = 'move',
}

type BasePayload = {
  source: MediaToCanvasDragSource;
  sourceNodeId: string;
  mode: MediaToCanvasDragMode;
  /** Screen px from ghost top-left to the cursor at dragstart (`setDragImage`); used to place the new node on drop. */
  previewOffsetX: number;
  previewOffsetY: number;
};

export type MediaToCanvasDragImportPayload = BasePayload & {
  source: MediaToCanvasDragSource.Import;
  fileIndex: number;
};

export type MediaToCanvasDragMediaIteratorPayload = BasePayload & {
  source: MediaToCanvasDragSource.MediaIterator;
  fileIndex: number;
};

export type MediaToCanvasDragAiModelPayload = BasePayload & {
  source: MediaToCanvasDragSource.AiModel;
  generationIndex: number;
};

export type MediaToCanvasDragPayload =
  MediaToCanvasDragImportPayload | MediaToCanvasDragMediaIteratorPayload | MediaToCanvasDragAiModelPayload;

export const MEDIA_TO_CANVAS_DRAG_MIME = 'application/x-weavy-media-to-canvas-drag';

export function setMediaToCanvasDragPayload(event: ReactDragEvent, payload: MediaToCanvasDragPayload): void {
  event.dataTransfer.setData(MEDIA_TO_CANVAS_DRAG_MIME, JSON.stringify(payload));
  event.dataTransfer.effectAllowed = 'copyMove';
}

function parseFileIndexSourcePayload(
  p: Record<string, unknown>,
  source: MediaToCanvasDragSource.Import | MediaToCanvasDragSource.MediaIterator,
): MediaToCanvasDragImportPayload | MediaToCanvasDragMediaIteratorPayload | null {
  if (p.mode !== MediaToCanvasDragMode.Duplicate && p.mode !== MediaToCanvasDragMode.Move) {
    return null;
  }
  const mode = p.mode;
  if (
    typeof p.sourceNodeId !== 'string' ||
    typeof p.fileIndex !== 'number' ||
    !Number.isInteger(p.fileIndex) ||
    p.fileIndex < 0
  ) {
    return null;
  }
  const previewOffset = parsePreviewOffset(p);
  if (!previewOffset) {
    return null;
  }
  return {
    source,
    sourceNodeId: p.sourceNodeId,
    fileIndex: p.fileIndex,
    mode,
    ...previewOffset,
  };
}

function parseAiModelPayload(p: Record<string, unknown>): MediaToCanvasDragAiModelPayload | null {
  if (p.mode !== MediaToCanvasDragMode.Duplicate && p.mode !== MediaToCanvasDragMode.Move) {
    return null;
  }
  const mode = p.mode;
  if (
    typeof p.sourceNodeId !== 'string' ||
    typeof p.generationIndex !== 'number' ||
    !Number.isInteger(p.generationIndex) ||
    p.generationIndex < 0
  ) {
    return null;
  }
  const previewOffset = parsePreviewOffset(p);
  if (!previewOffset) {
    return null;
  }
  return {
    source: MediaToCanvasDragSource.AiModel,
    sourceNodeId: p.sourceNodeId,
    generationIndex: p.generationIndex,
    mode,
    ...previewOffset,
  };
}

const MEDIA_TO_CANVAS_DRAG_SOURCE_VALUES = new Set<string>(Object.values(MediaToCanvasDragSource));

function isMediaToCanvasDragSource(value: string): value is MediaToCanvasDragSource {
  return MEDIA_TO_CANVAS_DRAG_SOURCE_VALUES.has(value);
}

function parsePreviewOffset(p: Record<string, unknown>): { previewOffsetX: number; previewOffsetY: number } | null {
  if (
    typeof p.previewOffsetX !== 'number' ||
    typeof p.previewOffsetY !== 'number' ||
    !Number.isFinite(p.previewOffsetX) ||
    !Number.isFinite(p.previewOffsetY) ||
    p.previewOffsetX < 0 ||
    p.previewOffsetY < 0
  ) {
    return null;
  }
  return { previewOffsetX: p.previewOffsetX, previewOffsetY: p.previewOffsetY };
}

export function getMediaDragPreviewDropScreenPosition(
  clientX: number,
  clientY: number,
  payload: MediaToCanvasDragPayload,
): { x: number; y: number } {
  return {
    x: clientX - payload.previewOffsetX,
    y: clientY - payload.previewOffsetY,
  };
}

export function parseMediaToCanvasDragPayload(raw: string): MediaToCanvasDragPayload | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const p = parsed as Record<string, unknown>;
    if (typeof p.source !== 'string' || !isMediaToCanvasDragSource(p.source)) return null;

    switch (p.source) {
      case MediaToCanvasDragSource.Import:
        return parseFileIndexSourcePayload(p, MediaToCanvasDragSource.Import);
      case MediaToCanvasDragSource.MediaIterator:
        return parseFileIndexSourcePayload(p, MediaToCanvasDragSource.MediaIterator);
      case MediaToCanvasDragSource.AiModel:
        return parseAiModelPayload(p);
    }
    return null;
  } catch {
    return null;
  }
}

export function getMediaToImportItemIndex(payload: MediaToCanvasDragPayload): number {
  return payload.source === MediaToCanvasDragSource.AiModel ? payload.generationIndex : payload.fileIndex;
}

export function getMediaToCanvasDragAnalyticsProps(payload: MediaToCanvasDragPayload): {
  source: MediaToCanvasDragSource;
  sourceNodeId: string;
  fileIndex?: number;
  generationIndex?: number;
} {
  if (payload.source === MediaToCanvasDragSource.AiModel) {
    return {
      source: payload.source,
      sourceNodeId: payload.sourceNodeId,
      generationIndex: payload.generationIndex,
    };
  }
  return {
    source: payload.source,
    sourceNodeId: payload.sourceNodeId,
    fileIndex: payload.fileIndex,
  };
}

export function getMediaToCanvasDragTrackEventName(mode: MediaToCanvasDragMode): string {
  return mode === MediaToCanvasDragMode.Duplicate
    ? MEDIA_TO_CANVAS_DRAG_EVENT_NAMES.DUPLICATE
    : MEDIA_TO_CANVAS_DRAG_EVENT_NAMES.MOVE;
}

/** Upper cap for drag ghost size (px); actual size is also limited by the grid tile on screen. */
const PREVIEW_ABS_MAX_PX = 180;
/** Default square box for the fallback when tile size is not available. */
const MEDIA_ICON_PREVIEW_PX = 96;
/** Static image fallback SVG in `public/`; video/3D fallbacks use inline SVG paths from app icons. */
const MEDIA_DRAG_PREVIEW_FALLBACK_SVG_URL = '/media-drag-preview-fallback.svg';
const SVG_NS = 'http://www.w3.org/2000/svg';

export type MediaToCanvasDragScreenRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type ActiveMediaToCanvasDrag = {
  sourceNodeId: string;
  duplicateModifier: boolean;
  /** Grid tile bounds at dragstart; a drop whose cursor lands back on this rect aborts the move/duplicate. */
  sourceTileRect: MediaToCanvasDragScreenRect | null;
  /** Dragged out of the full screen gallery rather than a node's grid (for analytics). */
  fromFullScreen?: boolean;
};

let activeMediaToCanvasDrag: ActiveMediaToCanvasDrag | null = null;
const DUPLICATE_MODIFIER_RELEASE_GRACE_MS = 300;
let duplicateModifierReleaseTimer: number | null = null;

function clearDuplicateModifierReleaseTimer(): void {
  if (duplicateModifierReleaseTimer !== null) {
    window.clearTimeout(duplicateModifierReleaseTimer);
    duplicateModifierReleaseTimer = null;
  }
}

export function beginMediaToCanvasDrag(session: ActiveMediaToCanvasDrag): void {
  clearDuplicateModifierReleaseTimer();
  window.removeEventListener('dragover', updateMediaToCanvasDragDropEffect);
  activeMediaToCanvasDrag = session;
  window.addEventListener('dragover', updateMediaToCanvasDragDropEffect);
}

export function updateMediaToCanvasDragDuplicateModifier(altKey: boolean): void {
  if (!activeMediaToCanvasDrag) {
    return;
  }

  if (altKey) {
    clearDuplicateModifierReleaseTimer();
    activeMediaToCanvasDrag.duplicateModifier = true;
    return;
  }

  if (!activeMediaToCanvasDrag.duplicateModifier || duplicateModifierReleaseTimer !== null) {
    return;
  }

  duplicateModifierReleaseTimer = window.setTimeout(() => {
    duplicateModifierReleaseTimer = null;
    if (activeMediaToCanvasDrag) {
      activeMediaToCanvasDrag.duplicateModifier = false;
    }
  }, DUPLICATE_MODIFIER_RELEASE_GRACE_MS);
}

export function endMediaToCanvasDrag(): void {
  clearDuplicateModifierReleaseTimer();
  window.removeEventListener('dragover', updateMediaToCanvasDragDropEffect);
  activeMediaToCanvasDrag = null;
}

/**
 * Sets the drop cursor for an active media-to-canvas drag. Registered as the window `dragover`
 * listener (covers the whole page) and also called from the canvas `dragover` handler.
 * `preventDefault` is required for the browser to honor `dropEffect` and fire `drop`.
 */
export function updateMediaToCanvasDragDropEffect(event: MediaToCanvasDragEffectEvent): void {
  if (!activeMediaToCanvasDrag) {
    return;
  }
  const { dataTransfer } = event;
  if (!dataTransfer?.types?.includes(MEDIA_TO_CANVAS_DRAG_MIME)) {
    return;
  }

  event.preventDefault();

  if (isMediaToCanvasDropOnDragSource(event.clientX, event.clientY, activeMediaToCanvasDrag.sourceNodeId)) {
    dataTransfer.dropEffect = 'none';
    return;
  }

  dataTransfer.dropEffect =
    resolveMediaToCanvasDropMode(event.altKey) === MediaToCanvasDragMode.Duplicate ? 'copy' : 'move';
}

export function getActiveMediaToCanvasDrag(): ActiveMediaToCanvasDrag | null {
  return activeMediaToCanvasDrag;
}

export function resolveMediaToCanvasDropMode(altKeyAtDrop: boolean): MediaToCanvasDragMode {
  if (altKeyAtDrop) {
    return MediaToCanvasDragMode.Duplicate;
  }
  return activeMediaToCanvasDrag?.duplicateModifier ? MediaToCanvasDragMode.Duplicate : MediaToCanvasDragMode.Move;
}

export function toMediaToCanvasDragScreenRect(rect: DOMRect): MediaToCanvasDragScreenRect {
  return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
}
/** Fallback cleanup when `dragend` never fires (some engines): ghost + drag session together. */
const DRAG_PREVIEW_ORPHAN_CLEANUP_MS = 120_000;

type PreviewBounds = { maxWidth: number; maxHeight: number };

function getGridTilePreviewBounds(tileRoot: HTMLElement | null): PreviewBounds {
  if (!tileRoot) {
    return { maxWidth: PREVIEW_ABS_MAX_PX, maxHeight: PREVIEW_ABS_MAX_PX };
  }
  const rect = tileRoot.getBoundingClientRect();
  const width = Math.max(rect.width, 1);
  const height = Math.max(rect.height, 1);
  if (width <= 1 && height <= 1) {
    return { maxWidth: MEDIA_ICON_PREVIEW_PX, maxHeight: MEDIA_ICON_PREVIEW_PX };
  }
  return {
    maxWidth: Math.min(width, PREVIEW_ABS_MAX_PX),
    maxHeight: Math.min(height, PREVIEW_ABS_MAX_PX),
  };
}

function trimPreviewUrl(url: string | undefined): string | null {
  const trimmed = url?.trim();
  return trimmed ? trimmed : null;
}

export type MediaDragPreviewFile = {
  type: string;
  thumbnailUrl?: string;
  url?: string;
  viewUrl?: string;
  blobstore?: { id?: string };
};

/**
 * URL for the drag ghost: `thumbnailUrl` when present. An Import image without a thumb falls back to
 * `url` (same as grid viewers). Iterator/model use thumb only. The ghost needs a picture already decoded
 * at dragstart, so a resolved video whose thumbnail is still transcoding gets nothing.
 */
export function getMediaDragPreviewUrl(
  file: MediaDragPreviewFile,
  source: MediaToCanvasDragSource,
  resolved: ResolvedAssets,
): string | null {
  const isVisualMedia = file.type === 'image' || file.type === 'video' || file.type === '3D';
  if (!isVisualMedia) {
    return null;
  }

  // Trimmed first so a blank stored thumbnail still reaches the url fallback, as it did before.
  const trimmed = {
    ...file,
    thumbnailUrl: trimPreviewUrl(file.thumbnailUrl) ?? undefined,
    url: trimPreviewUrl(file.url) ?? undefined,
  };
  return (
    assetThumbnailUrl(trimmed, resolved, {
      thumbnailOnly: source !== MediaToCanvasDragSource.Import,
    }) ?? null
  );
}

function getScaledPreviewDimensions(
  naturalWidth: number,
  naturalHeight: number,
  bounds: PreviewBounds,
): { width: number; height: number } {
  const scale = Math.min(bounds.maxWidth / naturalWidth, bounds.maxHeight / naturalHeight, 1);
  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: Math.max(1, Math.round(naturalHeight * scale)),
  };
}

function getIconPreviewSize(bounds: PreviewBounds): { width: number; height: number } {
  return { width: bounds.maxWidth, height: bounds.maxHeight };
}

function isDecodedImg(img: HTMLImageElement): boolean {
  return img.complete && img.naturalWidth > 0 && img.naturalHeight > 0;
}

function getCardCornerRadius(width: number, height: number): number {
  return Math.round((Math.min(width, height) * 7.059) / 120);
}

function getCardIconPx(width: number, height: number): number {
  return Math.max(16, Math.round((Math.min(width, height) * 28) / 120));
}

function createSvgIcon(paths: string[], iconPx: number): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', `${iconPx}`);
  svg.setAttribute('height', `${iconPx}`);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.display = 'block';
  svg.style.flexShrink = '0';

  paths.forEach((d) => {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.125');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
  });

  return svg;
}

function createFallbackIcon(kind: string | undefined, iconPx: number): HTMLElement | SVGSVGElement {
  if (kind === 'audio') {
    return createSvgIcon(SPEAKER_HIGH_ICON_PATHS, iconPx);
  }

  if (kind === 'text') {
    return createSvgIcon(TEXT_ICON_PATHS, iconPx);
  }

  if (kind === 'video') {
    return createSvgIcon(VIDEO_CAMERA_ICON_PATHS, iconPx);
  }

  if (kind === '3D') {
    return createSvgIcon(THREE_D_ICON_PATHS, iconPx);
  }

  const icon = document.createElement('img');
  icon.draggable = false;
  icon.src = MEDIA_DRAG_PREVIEW_FALLBACK_SVG_URL;
  icon.alt = '';
  icon.setAttribute('aria-hidden', 'true');
  icon.style.display = 'block';
  icon.style.width = `${iconPx}px`;
  icon.style.height = `${iconPx}px`;
  icon.style.flexShrink = '0';
  return icon;
}

/** Fallback card + icon; always rendered as the bottom layer of the drag ghost. */
function appendFallbackBackgroundLayer(
  parent: HTMLElement,
  width: number,
  height: number,
  fallbackKind?: string,
): void {
  const cornerRadius = getCardCornerRadius(width, height);
  const iconPx = getCardIconPx(width, height);

  const bg = document.createElement('div');
  bg.setAttribute('data-media-drag-preview-fallback', 'icon');
  bg.style.position = 'absolute';
  bg.style.inset = '0';
  bg.style.display = 'flex';
  bg.style.alignItems = 'center';
  bg.style.justifyContent = 'center';
  bg.style.backgroundColor = token.bg;
  bg.style.border = `1px solid ${color.White04_T}`;
  bg.style.borderRadius = `${cornerRadius}px`;
  bg.style.color = color.White;

  bg.appendChild(createFallbackIcon(fallbackKind, iconPx));
  parent.appendChild(bg);
}

function applyDragPreviewGhostPlacement(ghost: HTMLElement): void {
  ghost.style.pointerEvents = 'none';
  ghost.style.position = 'fixed';
  ghost.style.left = '-10000px';
  ghost.style.top = '0';
  ghost.style.zIndex = '2147483647';
  ghost.style.opacity = '1';
}

function imgSrc(img: HTMLImageElement): string {
  return img.currentSrc || img.src;
}

function getLoadedGridTileImg(tileRoot: HTMLElement | null, previewUrl: string): HTMLImageElement | null {
  const tileImg = tileRoot?.querySelector('img');
  if (!tileImg || !isDecodedImg(tileImg) || imgSrc(tileImg) !== previewUrl) {
    return null;
  }
  return tileImg;
}

function tryAppendThumbnailOverlay(
  parent: HTMLElement,
  src: string,
  naturalWidth: number,
  naturalHeight: number,
  cardSize: { width: number; height: number },
  options?: { trustDecoded?: boolean },
): void {
  const { width, height } = getScaledPreviewDimensions(naturalWidth, naturalHeight, {
    maxWidth: cardSize.width,
    maxHeight: cardSize.height,
  });
  const thumb = document.createElement('img');
  thumb.setAttribute('data-media-drag-preview-thumb', '');
  thumb.draggable = false;
  thumb.src = src;
  thumb.style.position = 'absolute';
  thumb.style.left = '50%';
  thumb.style.top = '50%';
  thumb.style.transform = 'translate(-50%, -50%)';
  thumb.style.display = 'block';
  thumb.style.objectFit = 'contain';
  thumb.style.width = `${width}px`;
  thumb.style.height = `${height}px`;
  thumb.style.zIndex = '1';
  thumb.style.pointerEvents = 'none';

  if (!options?.trustDecoded && !isDecodedImg(thumb)) {
    return;
  }
  parent.appendChild(thumb);
}

function tryAppendCanvasSnapshotOverlay(
  parent: HTMLElement,
  sourceCanvas: HTMLCanvasElement,
  cardSize: { width: number; height: number },
): boolean {
  const sourceWidth = sourceCanvas.width;
  const sourceHeight = sourceCanvas.height;
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return false;
  }

  const { width, height } = getScaledPreviewDimensions(sourceWidth, sourceHeight, {
    maxWidth: cardSize.width,
    maxHeight: cardSize.height,
  });

  const snapshot = document.createElement('canvas');
  snapshot.setAttribute('data-media-drag-preview-canvas', '');
  snapshot.width = width;
  snapshot.height = height;
  snapshot.style.position = 'absolute';
  snapshot.style.left = '50%';
  snapshot.style.top = '50%';
  snapshot.style.transform = 'translate(-50%, -50%)';
  snapshot.style.display = 'block';
  snapshot.style.width = `${width}px`;
  snapshot.style.height = `${height}px`;
  snapshot.style.zIndex = '1';
  snapshot.style.pointerEvents = 'none';

  try {
    const ctx = snapshot.getContext('2d');
    if (!ctx) {
      return false;
    }
    ctx.drawImage(sourceCanvas, 0, 0, width, height);
  } catch {
    return false;
  }

  parent.appendChild(snapshot);
  return true;
}

function resolveThumbnailForOverlay(
  previewUrl: string | null,
  tileRoot: HTMLElement | null,
): { src: string; naturalWidth: number; naturalHeight: number; trustDecoded: boolean } | null {
  if (!previewUrl) {
    return null;
  }

  const tileImg = getLoadedGridTileImg(tileRoot, previewUrl);
  if (tileImg) {
    return {
      src: imgSrc(tileImg),
      naturalWidth: tileImg.naturalWidth,
      naturalHeight: tileImg.naturalHeight,
      trustDecoded: true,
    };
  }

  const probe = document.createElement('img');
  probe.src = previewUrl;
  if (!isDecodedImg(probe)) {
    return null;
  }
  return {
    src: previewUrl,
    naturalWidth: probe.naturalWidth,
    naturalHeight: probe.naturalHeight,
    trustDecoded: false,
  };
}

/** Fallback card is always visible; decoded thumbnail is stacked on top when available at dragstart. */
function createLayeredDragPreviewGhost(
  previewUrl: string | null,
  tileRoot: HTMLElement | null,
  fallbackKind?: string,
): HTMLElement {
  const bounds = getGridTilePreviewBounds(tileRoot);
  const cardSize = getIconPreviewSize(bounds);
  const cornerRadius = getCardCornerRadius(cardSize.width, cardSize.height);

  const wrapper = document.createElement('div');
  wrapper.setAttribute('data-media-drag-preview', 'layered');
  wrapper.style.position = 'relative';
  wrapper.style.width = `${cardSize.width}px`;
  wrapper.style.height = `${cardSize.height}px`;
  wrapper.style.overflow = 'hidden';
  wrapper.style.flexShrink = '0';
  wrapper.style.borderRadius = `${cornerRadius}px`;
  wrapper.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.35)';

  appendFallbackBackgroundLayer(wrapper, cardSize.width, cardSize.height, fallbackKind);

  if (fallbackKind === '3D') {
    const tileCanvas = tileRoot?.querySelector('canvas');
    if (tileCanvas && tryAppendCanvasSnapshotOverlay(wrapper, tileCanvas, cardSize)) {
      return wrapper;
    }
  }

  const thumb = resolveThumbnailForOverlay(previewUrl, tileRoot);
  if (thumb) {
    tryAppendThumbnailOverlay(wrapper, thumb.src, thumb.naturalWidth, thumb.naturalHeight, cardSize, {
      trustDecoded: thumb.trustDecoded,
    });
  }

  return wrapper;
}

function getGhostLayoutPixels(ghost: HTMLElement): { width: number; height: number } {
  const width = ghost.offsetWidth || Number.parseInt(ghost.style.width, 10) || MEDIA_ICON_PREVIEW_PX;
  const height = ghost.offsetHeight || Number.parseInt(ghost.style.height, 10) || MEDIA_ICON_PREVIEW_PX;
  return { width: Math.max(1, width), height: Math.max(1, height) };
}

/** `setDragImage` offset: map cursor position within the grid tile onto the (smaller) ghost. */
function getDragPreviewOffset(
  event: Pick<ReactDragEvent, 'clientX' | 'clientY'>,
  ghost: HTMLElement,
  tileRoot: HTMLElement | null,
): { offsetX: number; offsetY: number } {
  const { width: ghostWidth, height: ghostHeight } = getGhostLayoutPixels(ghost);

  if (tileRoot) {
    const tileRect = tileRoot.getBoundingClientRect();
    const tileWidth = Math.max(tileRect.width, 1);
    const tileHeight = Math.max(tileRect.height, 1);
    return {
      offsetX: Math.min(ghostWidth, Math.max(0, ((event.clientX - tileRect.left) / tileWidth) * ghostWidth)),
      offsetY: Math.min(ghostHeight, Math.max(0, ((event.clientY - tileRect.top) / tileHeight) * ghostHeight)),
    };
  }

  return { offsetX: ghostWidth / 2, offsetY: ghostHeight / 2 };
}

function scheduleDragPreviewGhostCleanup(ghost: HTMLElement): void {
  let finished = false;
  const finish = () => {
    if (finished) {
      return;
    }
    finished = true;
    endMediaToCanvasDrag();
    ghost.remove();
  };
  window.addEventListener('dragend', finish, { capture: true, once: true });
  window.setTimeout(finish, DRAG_PREVIEW_ORPHAN_CLEANUP_MS);
}

export type MediaDragPreviewOffset = { offsetX: number; offsetY: number };

/** Same gap from the corner as the in-nodes mark on the Assets panel tiles. */
const COUNT_INSET_PX = 4;

/**
 * Adds the number of dragged files to the picture's top right corner. The card fits the picture
 * inside a square, so the corner is taken from the picture itself, or from the card when there
 * is only the fallback icon. The count looks like the FPL Badge in its brandFilled variant. The
 * picture is plain DOM and not React, so it takes the Badge's tokens and not the component.
 */
function addDragCount(card: HTMLElement, count: number): void {
  const cardWidth = Number.parseFloat(card.style.width) || 0;
  const cardHeight = Number.parseFloat(card.style.height) || 0;
  const picture = card.querySelector<HTMLElement>('[data-media-drag-preview-thumb], [data-media-drag-preview-canvas]');
  const pictureWidth = picture ? Number.parseFloat(picture.style.width) || cardWidth : cardWidth;
  const pictureHeight = picture ? Number.parseFloat(picture.style.height) || cardHeight : cardHeight;

  const badge = document.createElement('div');
  badge.setAttribute('data-media-drag-preview-count', String(count));
  badge.textContent = String(count);
  const { fontFamily, fontSize, fontWeight, letterSpacing } = fplTokens.text.body.medium;
  Object.assign(badge.style, {
    position: 'absolute',
    top: `${(cardHeight - pictureHeight) / 2 + COUNT_INSET_PX}px`,
    right: `${(cardWidth - pictureWidth) / 2 + COUNT_INSET_PX}px`,
    zIndex: '2',
    boxSizing: 'border-box',
    minWidth: '1rem',
    height: '1rem',
    padding: `0 ${fplTokens.spacer.x1}`,
    borderRadius: fplTokens.radius.medium,
    background: fplTokens.color.bgBrand,
    color: fplTokens.color.textOnbrand,
    fontFamily,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight: '1rem',
    textAlign: 'center',
  });
  card.appendChild(badge);
}

/** `count` above one adds the number of dragged files to the picture. */
export function setMediaToCanvasDragPreview(
  event: ReactDragEvent,
  previewUrl: string | null,
  fallbackKind?: string,
  count = 1,
): MediaDragPreviewOffset {
  const tileRoot = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
  const ghost = createLayeredDragPreviewGhost(previewUrl, tileRoot, fallbackKind);
  if (count > 1) {
    addDragCount(ghost, count);
  }

  applyDragPreviewGhostPlacement(ghost);
  document.body.appendChild(ghost);
  scheduleDragPreviewGhostCleanup(ghost);

  const { offsetX, offsetY } = getDragPreviewOffset(event, ghost, tileRoot);
  event.dataTransfer.setDragImage(ghost, offsetX, offsetY);
  return { offsetX, offsetY };
}

export function isMediaToCanvasDragGridActive(featureEnabled: boolean, fileCount: number): boolean {
  return featureEnabled && fileCount > 1;
}

export type MediaToCanvasGridDragSession = {
  index: number;
  duplicateModifier: boolean;
};

export function isMediaToCanvasGridDragSourceDimmed(
  fileGlobalIndex: number,
  session: MediaToCanvasGridDragSession | null,
): boolean {
  return session !== null && fileGlobalIndex === session.index && !session.duplicateModifier;
}

export function isAssetKindDraggableToCanvas(kind: string): boolean {
  switch (kind) {
    case 'image':
    case 'video':
    case '3D':
    case 'audio':
    case 'text':
      return true;
    default:
      return false;
  }
}

function isPointInScreenRect(clientX: number, clientY: number, rect: MediaToCanvasDragScreenRect): boolean {
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
}

export function isMediaToCanvasDropOnSourceNode(clientX: number, clientY: number, sourceNodeId: string): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const el = document.elementFromPoint(clientX, clientY);
  if (!el) {
    return false;
  }
  if (el.closest('.react-flow__edge') || el.closest('.react-flow__edges')) {
    return false;
  }
  const nodeEl = el.closest('.react-flow__node');
  if (!nodeEl) {
    return false;
  }
  return nodeEl.getAttribute('data-id') === sourceNodeId;
}

/**
 * True when the cursor is back over the drag's own source tile — used to cancel the drop (show `none`)
 * so releasing on the origin is a no-op. Keyed on the cursor, not the ghost footprint, so dropping
 * just clear of the source still creates a node. Falls back to the source node bounds when the tile
 * rect is unknown.
 */
export function isMediaToCanvasDropOnDragSource(clientX: number, clientY: number, sourceNodeId: string): boolean {
  const drag = getActiveMediaToCanvasDrag();
  // Out of full screen the source node was hidden behind the gallery the whole drag, so landing on it isn't a
  // deliberate "put it back"; the drop still adds its copy.
  if (drag?.sourceNodeId === sourceNodeId && drag.fromFullScreen) {
    return false;
  }
  if (drag?.sourceNodeId === sourceNodeId && drag.sourceTileRect) {
    return isPointInScreenRect(clientX, clientY, drag.sourceTileRect);
  }
  return isMediaToCanvasDropOnSourceNode(clientX, clientY, sourceNodeId);
}
